/**
 * What the Costs page shows (RUT-4073): the month's costs split into what each
 * customer causes (direct) and what everyone shares, each direct cost's unit
 * cost, the customers who cost the most to serve, and the vendor free tiers we
 * are closest to leaving.
 *
 * The split itself is decided on the server (`splitCosts` in
 * apps/api/src/handlers/adminInsights/businessHealth.ts). This module only
 * arranges it for the page, and is unit tested so the page renders nothing it
 * computed on its own.
 */

import type {
  DirectCost,
  DirectUnits,
  FreeTierWatchItem,
  OtherEnvironmentsStatus,
  OverviewMonth,
  OverviewResponse,
} from './types';
import { formatCents, formatCount, formatCurrency, formatMonth, formatPercent } from './format';
import { biggestDriver, changeFrom } from './businessHealth';

/** A cost divided by the units behind it; null when there were no units. */
export function unitCost(cost: number, units: number): number | null {
  return units > 0 ? cost / units : null;
}


export type DirectKind = 'texts' | 'visits' | 'ai' | 'gps';

type UnitKey = 'textSegments' | 'visits' | 'aiRequests' | 'crewHours' | 'gpsSegments';

const DIRECT: Array<{ key: DirectKind; label: string; unit: (u: DirectUnits) => [UnitKey, string] }> = [
  { key: 'texts', label: 'Texts', unit: () => ['textSegments', 'a segment'] },
  { key: 'visits', label: 'Visit processing', unit: () => ['visits', 'a visit'] },
  // Per AI call: every token kind we pay for, cache reads included, lands in it.
  { key: 'ai', label: 'AI assistant', unit: () => ['aiRequests', 'an AI request'] },
  // A segment is one crew member's minute of GPS — one write. Past the location
  // table's 90 days the segments are gone, and the split falls back to crew hours.
  {
    key: 'gps',
    label: 'Crew GPS',
    unit: (u) => (u.gpsBasis === 'segments' ? ['gpsSegments', 'a crew GPS minute'] : ['crewHours', 'a crew hour']),
  },
];

export interface CostRow {
  key: string;
  label: string;
  /** Unit cost or what the line is, in a few words. */
  note: string;
  /** One value per month asked for; null when the month has no reading (not connected). */
  values: Array<number | null>;
}

/**
 * The direct-cost table: one row per kind, with the unit cost of the last
 * month asked for, and a total row.
 */
export function directCostRows(months: OverviewMonth[]): { rows: CostRow[]; total: CostRow } {
  const latest = months[months.length - 1];
  const rows = DIRECT.map(({ key, label, unit }) => {
    const [unitKey, per] = unit(latest.directUnits);
    const cost = unitCost(latest.directCost[key], latest.directUnits[unitKey]);
    return {
      key,
      label,
      note: cost === null ? `none used in ${formatMonth(latest.month)}` : `${formatCents(cost)} ${per}`,
      values: months.map((m) => m.directCost[key]),
    };
  });
  return {
    rows,
    total: { key: 'total', label: 'Total', note: '', values: months.map((m) => m.directCost.total) },
  };
}

/** The shared-cost table. Staging and dev read null for a month they were not read. */
export function sharedCostRows(
  months: OverviewMonth[],
  status: OtherEnvironmentsStatus,
  flatVendors: string[] = [],
): { rows: CostRow[]; total: CostRow } {
  const otherNote =
    status === 'connected'
      ? 'their own AWS accounts'
      : status === 'unavailable'
        ? 'could not be read this time — left out'
        : 'not connected yet — left out';
  const rows: CostRow[] = [
    {
      key: 'alwaysOn',
      label: 'Always-on AWS',
      note: 'production background jobs, logs, database floor',
      values: months.map((m) => m.sharedCost.alwaysOn),
    },
    {
      key: 'otherEnvironments',
      label: 'Staging and dev AWS',
      note: otherNote,
      values: months.map((m) => (status === 'connected' ? m.sharedCost.otherEnvironments : null)),
    },
    {
      key: 'vendors',
      label: 'Search and other flat vendors',
      note: flatVendors.length > 0 ? `flat monthly bills (${flatVendors.join(', ')})` : 'flat monthly bills',
      values: months.map((m) => m.sharedCost.vendors),
    },
    {
      key: 'textingSetup',
      label: 'Texting setup',
      note: 'campaign fee and phone numbers',
      values: months.map((m) => m.sharedCost.textingSetup),
    },
    {
      key: 'nonPaying',
      label: 'Companies not counted',
      note: 'demo, never billed, switched off',
      values: months.map((m) => m.sharedCost.nonPaying),
    },
  ];
  return {
    rows,
    total: { key: 'total', label: 'Total', note: '', values: months.map((m) => m.sharedCost.total) },
  };
}

function driverLabel(cost: DirectCost): string {
  const driver = biggestDriver(cost);
  return driver ? `${driver.label} ${formatCurrency(driver.value)}` : '';
}

export interface CostlyCustomer {
  orgId: string;
  name: string;
  driver: string;
  directCost: number;
  revenueToRuta: number;
  subscriptionMargin: number;
  /** Subscription margin ÷ revenue; null with no revenue. */
  marginPct: number | null;
}

/**
 * The customers who cost the most to serve in a month, next to what they
 * bring in. Only companies the month counts — the demo account and companies
 * never billed are shared cost, not customers.
 */
export function costliestCustomers(data: OverviewResponse, month: string, limit = 5): CostlyCustomer[] {
  return data.byOrg
    .map((org) => ({ org, m: org.byMonth.find((x) => x.month === month) }))
    .filter(({ m }) => m?.counted && m.directCost.total > 0)
    .map(({ org, m }) => ({
      orgId: org.orgId,
      name: org.name,
      driver: driverLabel(m!.directCost),
      directCost: m!.directCost.total,
      revenueToRuta: m!.revenueToRuta,
      subscriptionMargin: m!.subscriptionMargin,
      marginPct: m!.revenueToRuta > 0 ? m!.subscriptionMargin / m!.revenueToRuta : null,
    }))
    .sort((a, b) => b.directCost - a.directCost)
    .slice(0, limit);
}

export interface CostSummary {
  direct: number;
  /** Direct cost per counted company; null with none. */
  directEach: number | null;
  shared: number;
  sharedEach: number | null;
  total: number;
  /** Change in total from the month before, as a fraction; null without one. */
  change: number | null;
  /** Total cost ÷ revenue to Ruta; null with no revenue. */
  shareOfRevenue: number | null;
}

/** The four headline figures for one month. */
export function costSummary(month: OverviewMonth, prior: OverviewMonth | null): CostSummary {
  const total = month.directCost.total + month.sharedCost.total;
  const priorTotal = prior ? prior.directCost.total + prior.sharedCost.total : 0;
  const counted = month.countedOrgs;
  return {
    direct: month.directCost.total,
    directEach: counted > 0 ? month.directCost.total / counted : null,
    shared: month.sharedCost.total,
    sharedEach: counted > 0 ? month.sharedCost.total / counted : null,
    total,
    change: prior ? changeFrom(priorTotal, total) : null,
    shareOfRevenue: month.revenueToRuta > 0 ? total / month.revenueToRuta : null,
  };
}

function formatDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** How close a free tier is, in words: "82% of 1,000,000 events a month". */
export function freeTierReading(item: FreeTierWatchItem): { headline: string; detail: string } {
  const limit = `${formatCount(item.limit)} ${item.unit} a ${item.per}`;
  if (item.usedShare === null || item.used === null) {
    return { headline: 'Not measured', detail: `Limit ${limit}. Our usage meter does not count this.` };
  }
  const when =
    item.basis === 'peak_day'
      ? item.period
        ? `busiest day in the last 30, ${formatDay(item.period)}`
        : 'none used in the last 30 days'
      : item.basis === 'month_so_far'
        ? `${formatMonth(item.period!)} so far`
        : `all of ${formatMonth(item.period!)}`;
  return {
    headline: `${formatPercent(item.usedShare, 0)} of ${limit}`,
    detail: `${formatCount(item.used)} ${item.unit}, ${when}${item.meterGap ? ` (a floor: ${item.meterGap})` : ''}.`,
  };
}

/** How close is close: over 75% of a limit is a warning, over 100% is past it. */
export function freeTierTone(item: FreeTierWatchItem): 'past' | 'close' | 'fine' | 'unknown' {
  if (item.usedShare === null) return 'unknown';
  if (item.usedShare >= 1) return 'past';
  if (item.usedShare >= 0.75) return 'close';
  return 'fine';
}
